import { selectAudioStore, type LocalStorePort } from '../../platform/local-store';

/**
 * TEMPORARY CAPTURE AUDIO — M4 / S-04-02, under D-010.
 *
 * S-04-01 made the TEXT of an unfinished Capture survive a process kill. This
 * module does the same for the one thing text cannot replace: **a recording
 * that was safely finished and has not become a transcript yet.** Record, Stop,
 * then the OS kills Crewlink before Transcribe — until now, the words Brandon
 * spoke on a roof in the rain were simply gone.
 *
 * WHAT IS STORED, AND WHAT EMPHATICALLY IS NOT:
 *
 *   **ONLY SAFELY FINALIZED AUDIO.** A recording still in progress is never
 *   written here — no chunks, no streaming, no microphone journal. D-019 (J-1)
 *   decided that a process death mid-recording loses the audio and says so
 *   honestly, and nothing in this file weakens that. The only Blob that ever
 *   reaches this store is one `recorder.stop()` finalized cleanly.
 *
 *   **NO TRANSCRIPT, NO PROJECT METADATA, NO AI FIELDS, NO TOKENS, NO SERVER
 *   IDENTIFIERS, NO PLAYBACK OR HISTORY STATE.** The draft envelope already
 *   owns the Project, the note and the status; duplicating them here would
 *   create a second place for them to disagree. The record carries exactly what
 *   is needed to prove this Blob belongs to this Capture and when it expires.
 *
 * THE POINTER IS THE AUTHORITY, NOT THIS RECORD.
 *
 *   Unresolved audio exists if and only if `draft.pendingAudioId` names it AND
 *   a matching, valid record is here. **The existence of a record is never
 *   authority on its own** — a leftover Blob whose id nothing points at is an
 *   orphan, and an orphan is invisible to the product. That asymmetry is what
 *   makes the delete-after-transcript ordering safe: once the draft says
 *   `pendingAudioId: null`, whatever is still on disk is inert even if the
 *   delete that should have followed never happened.
 *
 * ONE UNRESOLVED RECORDING AT A TIME. The store is keyed by owner, so there is
 * exactly one slot per user and no queue to build, mismanage or leak. A Capture
 * that records, transcribes and records again produces a NEW `audioId` for the
 * second recording — which is why the pointer is an id and not a boolean: the
 * Capture keeps one `client_capture_uuid` across both, so only the audio id can
 * tell them apart.
 *
 * D-010'S WINDOW IS A UX TRANSITION, NOT A DELETE TRIGGER. Expiry is absolute —
 * anchored to when the recording was finalized, never extended by looking at
 * it — and reaching it deletes NOTHING. It changes what Brandon is told and
 * narrows his choices to Retry or Discard. Silently purging at 72 hours would
 * be the product destroying his only copy while he was not watching, which is
 * precisely what D-010 forbids.
 */

/** Bump only with a deliberate record change. Any other version is treated as absent. */
export const CAPTURE_AUDIO_SCHEMA_VERSION = 1;

/** One slot per authenticated user. The owner id is the key. */
export const CAPTURE_AUDIO_KEY_PREFIX = 'capture-audio:';

export function captureAudioKey(ownerUserId: string): string {
  return `${CAPTURE_AUDIO_KEY_PREFIX}${ownerUserId}`;
}

/**
 * D-010's finite recovery window. **A tunable policy value, not architecture** —
 * the decision's own words. Roughly 72 hours.
 */
export const CAPTURE_AUDIO_RECOVERY_WINDOW_MS = 72 * 60 * 60 * 1000;

export interface CaptureAudioRecord {
  readonly schemaVersion: number;
  readonly ownerUserId: string;
  /** Unique per finalized recording — NOT per Capture. See the module note. */
  readonly audioId: string;
  /** The Capture this recording belongs to. Checked against the draft on read. */
  readonly clientCaptureUuid: string;
  readonly mimeType: string;
  readonly blob: Blob;
  /** Written at store time and re-checked on read — a cheap integrity proof. */
  readonly byteSize: number;
  readonly finalizedAtMs: number;
  /** Absolute, computed once from `finalizedAtMs`. Never extended by access. */
  readonly expiresAtMs: number;
}

/**
 * The closed key set. Exported so the security test can assert that a stored
 * record contains these and nothing else — no token, no transcript, no
 * Project, no server id.
 */
export const CAPTURE_AUDIO_FIELDS = [
  'schemaVersion',
  'ownerUserId',
  'audioId',
  'clientCaptureUuid',
  'mimeType',
  'blob',
  'byteSize',
  'finalizedAtMs',
  'expiresAtMs',
] as const;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * A stored record, validated field by field, or `null`.
 *
 * **STRICT, AND FAILING MEANS "THERE IS NO RECORDING" — never "repair it".**
 * Every check below is a way a Blob could be present but untrustworthy, and
 * offering an untrustworthy recording is worse than admitting the loss: Brandon
 * would tap Transcribe, wait, and get nothing, having already been told his
 * words were safe.
 *
 * `blob.size === byteSize` is the one that earns its keep. A truncated write, a
 * quota kill mid-transaction or a partially restored profile all produce a Blob
 * that EXISTS and is short. Size was recorded at write time by the code that
 * had the whole thing, so a mismatch is proof of damage rather than a guess.
 */
export function parseCaptureAudioRecord(
  value: unknown,
  expectedOwnerUserId: string,
): CaptureAudioRecord | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;

  if (raw.schemaVersion !== CAPTURE_AUDIO_SCHEMA_VERSION) return null;
  if (!isNonEmptyString(raw.ownerUserId)) return null;
  // The owner check, where it actually protects: a record is only ever handed
  // back to the identity it was written under.
  if (raw.ownerUserId !== expectedOwnerUserId) return null;
  if (!isNonEmptyString(raw.audioId)) return null;
  if (!isNonEmptyString(raw.clientCaptureUuid)) return null;
  if (typeof raw.mimeType !== 'string') return null;
  if (!isFiniteNumber(raw.byteSize)) return null;
  if (!isFiniteNumber(raw.finalizedAtMs)) return null;
  if (!isFiniteNumber(raw.expiresAtMs)) return null;

  const blob: unknown = raw.blob;
  // `instanceof Blob` rather than a duck-typed size check: a JSON round trip
  // turns a Blob into `{}`, and `{}` must not pass for audio.
  if (!(blob instanceof Blob)) return null;
  if (blob.size <= 0) return null;
  if (blob.size !== raw.byteSize) return null;

  return {
    schemaVersion: CAPTURE_AUDIO_SCHEMA_VERSION,
    ownerUserId: raw.ownerUserId,
    audioId: raw.audioId,
    clientCaptureUuid: raw.clientCaptureUuid,
    mimeType: raw.mimeType,
    blob,
    byteSize: raw.byteSize,
    finalizedAtMs: raw.finalizedAtMs,
    expiresAtMs: raw.expiresAtMs,
  };
}

/** Whether D-010's window has been reached. Reaching it deletes nothing. */
export function isCaptureAudioExpired(record: CaptureAudioRecord, nowMs: number): boolean {
  return nowMs >= record.expiresAtMs;
}

/**
 * What a draft and a stored record, taken TOGETHER, mean.
 *
 * `none`    — the draft points at no recording. The store may still hold a
 *             record; it is an orphan and is invisible to the product.
 * `ready`   — pointer and record agree and the record is valid and in window.
 * `expired` — the same, past D-010's window. **The audio is untouched.**
 * `lost`    — the draft points at a recording that is missing, corrupt, or
 *             belongs to another Capture. Honest loss, never a silent repair.
 */
export type CaptureAudioMatch = 'none' | 'ready' | 'expired' | 'lost';

/**
 * THE OFFER RULE, in one pure function — M4 / S-04-02.
 *
 * Everything the product asks about unresolved audio comes through here, so
 * the rule cannot drift between the recovery card and the Capture screen. Read
 * it as the list of things that must ALL hold before a recording is offered
 * back: the draft points at an id, a record exists, it is that id, it belongs
 * to this owner, it belongs to this Capture, and it passed integrity checks.
 *
 * **The pointer leads.** A record with no pointer is `none` — never `ready` —
 * which is what makes a leftover Blob after a transcript inert rather than a
 * second chance to transcribe something already turned into text. And a
 * pointer with no valid record is `lost` rather than ignored, because the
 * difference between "there was never a recording" and "your recording is
 * gone" is a difference Brandon deserves to be told about.
 */
export function matchCaptureAudio(
  draft: { pendingAudioId: string | null; clientCaptureUuid: string; ownerUserId: string },
  record: CaptureAudioRecord | null,
  nowMs: number,
): CaptureAudioMatch {
  if (draft.pendingAudioId === null) return 'none';
  if (record === null) return 'lost';
  if (record.audioId !== draft.pendingAudioId) return 'lost';
  if (record.ownerUserId !== draft.ownerUserId) return 'lost';
  if (record.clientCaptureUuid !== draft.clientCaptureUuid) return 'lost';
  return isCaptureAudioExpired(record, nowMs) ? 'expired' : 'ready';
}

/**
 * Whether a stored record is an ORPHAN — safe to remove.
 *
 * The definition is deliberately narrow: an audio record is an orphan when a
 * **positively read** owner draft proves its id is not the authoritative
 * `pendingAudioId`. **Nothing else qualifies.** Not note text, not an empty
 * note, not navigation, not age, not `recordingInProgress`. Every one of those
 * would eventually delete a recording somebody was still going to transcribe.
 *
 * **`draft === null` is NEVER deletion authority** (R1 S-04-02 finding F-2).
 * The draft store fails by resolving `null`, so at this layer "there is no
 * draft" and "the draft could not be read" are indistinguishable — and the
 * only safe reading of an ambiguous answer is the one that deletes nothing.
 * A genuinely draftless leftover Blob costs a few hundred kilobytes and is
 * invisible to the product (the offer rule requires the pointer); it is
 * overwritten by the next recording and purged by sign-out. Deleting a
 * recording because a read glitched would cost Brandon his words.
 */
export function isOrphanCaptureAudio(
  record: CaptureAudioRecord,
  draft: { pendingAudioId: string | null } | null,
): boolean {
  return draft !== null && draft.pendingAudioId !== record.audioId;
}

export interface CaptureAudioStore {
  /** This user's temporary recording, or `null`. Never throws, never deletes. */
  read(ownerUserId: string): Promise<CaptureAudioRecord | null>;
  /** Resolves once the Blob is COMMITTED to durable storage. */
  write(record: CaptureAudioRecord): Promise<void>;
  /** Removes this user's temporary recording. */
  clear(ownerUserId: string): Promise<void>;
  /**
   * Whether the underlying store survives process death.
   *
   * **Read it after an operation has resolved.** S-04-03 made this live rather
   * than a value copied at construction: the IndexedDB adapter only learns its
   * open failed once that open settles, so a snapshot taken when the store was
   * built is the optimistic answer frozen in place.
   */
  readonly durable: boolean;
}

/**
 * `write` and `clear` DELIBERATELY LET THE PORT'S FAILURE SURFACE as a
 * resolved-but-unverified call, and the caller checks by reading back. The
 * port itself never throws (it degrades to no-ops), so the Capture screen
 * confirms a write landed rather than trusting that it did — a recording
 * claimed as kept and silently dropped is the exact lie S-04-02 exists to
 * prevent.
 */
export function createCaptureAudioStore(store: LocalStorePort): CaptureAudioStore {
  return {
    get durable() {
      return store.durable;
    },

    async read(ownerUserId) {
      // No identity, no read. There is no anonymous recording.
      if (!isNonEmptyString(ownerUserId)) return null;
      const record = await store.get(captureAudioKey(ownerUserId));
      return parseCaptureAudioRecord(record, ownerUserId);
    },

    async write(record) {
      if (!isNonEmptyString(record.ownerUserId)) return;
      // Assembled field by field rather than spread, so a caller cannot smuggle
      // a transcript, a token or a Project into the audio store by attaching it
      // to the object.
      const stored: Record<(typeof CAPTURE_AUDIO_FIELDS)[number], unknown> = {
        schemaVersion: CAPTURE_AUDIO_SCHEMA_VERSION,
        ownerUserId: record.ownerUserId,
        audioId: record.audioId,
        clientCaptureUuid: record.clientCaptureUuid,
        mimeType: record.mimeType,
        blob: record.blob,
        byteSize: record.byteSize,
        finalizedAtMs: record.finalizedAtMs,
        expiresAtMs: record.expiresAtMs,
      };
      await store.set(captureAudioKey(record.ownerUserId), stored);
    },

    async clear(ownerUserId) {
      if (!isNonEmptyString(ownerUserId)) return;
      await store.remove(captureAudioKey(ownerUserId));
    },
  };
}

/**
 * Build the record for a safely finalized recording.
 *
 * `nowMs` is injected rather than read from the clock so expiry boundaries are
 * testable to the millisecond without waiting three days and without a
 * production test backdoor. Expiry is computed ONCE, here, from the moment the
 * recording finished — never from when it was last looked at.
 */
export function buildCaptureAudioRecord(input: {
  ownerUserId: string;
  audioId: string;
  clientCaptureUuid: string;
  blob: Blob;
  nowMs: number;
}): CaptureAudioRecord {
  return {
    schemaVersion: CAPTURE_AUDIO_SCHEMA_VERSION,
    ownerUserId: input.ownerUserId,
    audioId: input.audioId,
    clientCaptureUuid: input.clientCaptureUuid,
    mimeType: input.blob.type,
    blob: input.blob,
    byteSize: input.blob.size,
    finalizedAtMs: input.nowMs,
    expiresAtMs: input.nowMs + CAPTURE_AUDIO_RECOVERY_WINDOW_MS,
  };
}

let applicationAudioStore: CaptureAudioStore | null = null;

export function getCaptureAudioStore(): CaptureAudioStore {
  applicationAudioStore ??= createCaptureAudioStore(selectAudioStore());
  return applicationAudioStore;
}
